// Aba Cartão: Brick `CardPayment` do Mercado Pago. Número, validade e CVV
// ficam nos iframes do MP — o Pulse só recebe o `token` no onSubmit.
// O botão do próprio Brick é o primário ("Assinar · R$ X", via
// `visual.texts.formSubmit`), assim o submit continua validado pelo MP.
//
// O CardPayment remonta o Brick sempre que `initialization`, `customization`
// ou os callbacks mudam de identidade (efeito com essas deps no SDK): tudo
// aqui é memoizado e o onSubmit lê o handler atual por ref.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CardPayment, initMercadoPago } from "@mercadopago/sdk-react";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import { AEROS, stateText } from "../../styles/aerosTokens";

// initMercadoPago uma vez por public key (troca de chave nas Configurações
// re-inicializa na próxima abertura).
let initializedKey = null;
const ensureMercadoPago = (publicKey) => {
  if (initializedKey === publicKey) return;
  initMercadoPago(publicKey, { locale: "pt-BR" });
  initializedKey = publicKey;
};

// Textos do Brick no idioma da interface (locales aceitos pelo MP).
const brickLocale = () => {
  const lang = String(i18n.language || "pt").slice(0, 2);
  if (lang === "en") return "en-US";
  if (lang === "es") return "es-AR";
  return "pt-BR";
};

const useStyles = makeStyles((theme) => ({
  // O Brick tem padding próprio; o negativo alinha com a coluna.
  brick: { minHeight: 120, margin: "0 -4px" },
  error: {
    fontSize: 12.5,
    color: stateText(theme, AEROS.error, "error"),
    lineHeight: 1.5,
  },
}));

const CardPanel = ({ publicKey, amount, maxInstallments, submitLabel, onSubmit }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isLight = theme.palette.type === "light";
  const [loadError, setLoadError] = useState(false);

  ensureMercadoPago(publicKey);

  const submitRef = useRef(onSubmit);
  useEffect(() => {
    submitRef.current = onSubmit;
  }, [onSubmit]);

  // Campos sensíveis são iframes do MP: abaixo de 600px a fonte vai a 16px
  // para o iOS não dar zoom ao focar.
  const [mobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 600
  );

  const initialization = useMemo(() => ({ amount: Number(amount) }), [amount]);

  const customization = useMemo(
    () => ({
      paymentMethods: { maxInstallments, minInstallments: 1 },
      visual: {
        hideFormTitle: true,
        texts: { formSubmit: submitLabel },
        style: isLight
          ? {
              theme: "default",
              customVariables: {
                baseColor: theme.palette.primary.main,
                fontSizeMedium: mobile ? "16px" : "13.5px",
                borderRadiusMedium: "10px",
              },
            }
          : {
              theme: "dark",
              customVariables: {
                baseColor: "#A044FF",
                formBackgroundColor: "#0E1119",
                inputBackgroundColor: "#12151F",
                textPrimaryColor: "#F1F2F6",
                textSecondaryColor: "#9CA3B8",
                outlinePrimaryColor: AEROS.cyan,
                errorColor: AEROS.error,
                successColor: AEROS.success,
                fontSizeMedium: mobile ? "16px" : "13.5px",
                borderRadiusMedium: "10px",
                inputFocusedBoxShadow: "0 0 0 3px rgba(34,211,238,0.15)",
              },
            },
      },
    }),
    [maxInstallments, submitLabel, isLight, mobile, theme.palette.primary.main]
  );

  // `additionalData` traz `lastFourDigits` (troca de cartão mostra o final).
  const handleSubmit = useCallback(
    (formData, additionalData) => submitRef.current(formData, additionalData),
    []
  );
  const handleError = useCallback((error) => {
    // Erros de campo (cartão inválido etc.) o Brick mostra sozinho; só o
    // que impede o formulário de existir vira aviso aqui.
    if (error?.type === "critical") setLoadError(true);
  }, []);
  const handleReady = useCallback(() => setLoadError(false), []);

  return (
    <>
      {loadError && (
        <div className={classes.error} role="alert">
          {i18n.t("subscription.checkout.cardLoadError")}
        </div>
      )}
      <div className={classes.brick}>
        <CardPayment
          initialization={initialization}
          customization={customization}
          onSubmit={handleSubmit}
          onReady={handleReady}
          onError={handleError}
          locale={brickLocale()}
        />
      </div>
    </>
  );
};

export default CardPanel;
